/**
 * Rejestr pól WYLICZANYCH procesu — pola przekrojowe list liczone przez silnik
 * list (nazwa aktywnego procesu + bieżący etap). NIE mają fizycznej kolumny i NIE
 * są polami dodatkowymi (ext) tenanta — to trzecia kategoria: pola wyliczane,
 * tylko-do-odczytu.
 *
 * DLACZEGO W KODZIE, A NIE W KATALOGU BAZY (`system_module_fields`): katalog jest
 * czytany BEZ filtra przez trzy niezależne kreatory (Listy, Procesów, Formularza).
 * Wpisanie tam pola wyliczanego wyciekało je do WSZYSTKICH trzech — m.in. dało się
 * ustawić „Aktywny proces" jako pole wymagane procesu, którego nigdy nie da się
 * wypełnić (ADR-0093). Trzymając je w kodzie, każdy konsument decyduje sam: Kreator
 * Listy dokleja je jako opcjonalne kolumny (przez `mergeComputedProcessFields`),
 * Kreator Formularza pokaże je jako readonly (osobny krok), Kreator Procesów ich nie
 * widzi wcale.
 *
 * Etykiety reużywają istniejących kluczy i18n (tych samych, co kolumny list w
 * `list-column-registry.ts`) — bramka `check:i18n` pilnuje ich istnienia.
 */

export interface ComputedProcessField {
  /** Slug pola — spójny z aliasem kolumny silnika list i `CORE_COLUMN_ID_TO_SLUG` stron. */
  slug: string;
  /** Klucz i18n etykiety (reużyty z rejestru kolumn list). */
  labelKey: string;
  /** Typ pola — dla konsumentów oczekujących kształtu pola katalogu (np. paleta formularza). */
  fieldType: string;
}

/**
 * Pola wyliczane procesu per slug modułu systemowego. Brak wpisu = moduł ich nie ma.
 * Wszystkie 7 klas dokumentów (faktury zakupu/sprzedaży, umowy, pisma, dok. handlowe,
 * wewnętrzne, specjalne) niosą te pola — silnik list liczy je dla każdej z nich.
 * Klucz = slug kartoteki systemowej klasy (`system_modules.slug`, patrz `CLASS_KARTOTEKA_SLUGS`).
 */
const COMPUTED_PROCESS_FIELDS: Record<string, ComputedProcessField[]> = {
  purchase_invoices: [
    {
      slug: 'active_process_name',
      labelKey: 'tenant.purchaseInvoices.columns.activeProcessName',
      fieldType: 'varchar',
    },
    {
      slug: 'current_stage',
      labelKey: 'tenant.purchaseInvoices.columns.currentStage',
      fieldType: 'varchar',
    },
  ],
  sale_invoices: [
    {
      slug: 'active_process_name',
      labelKey: 'tenant.saleInvoices.columns.activeProcessName',
      fieldType: 'varchar',
    },
    {
      slug: 'current_stage',
      labelKey: 'tenant.saleInvoices.columns.currentStage',
      fieldType: 'varchar',
    },
  ],
  contracts: [
    {
      slug: 'active_process_name',
      labelKey: 'tenant.documents.contracts.columns.activeProcessName',
      fieldType: 'varchar',
    },
    {
      slug: 'current_stage',
      labelKey: 'tenant.documents.contracts.columns.currentStage',
      fieldType: 'varchar',
    },
  ],
  letters: [
    {
      slug: 'active_process_name',
      labelKey: 'tenant.documents.letters.columns.activeProcessName',
      fieldType: 'varchar',
    },
    {
      slug: 'current_stage',
      labelKey: 'tenant.documents.letters.columns.currentStage',
      fieldType: 'varchar',
    },
  ],
  commercial: [
    {
      slug: 'active_process_name',
      labelKey: 'tenant.documents.commercial.columns.activeProcessName',
      fieldType: 'varchar',
    },
    {
      slug: 'current_stage',
      labelKey: 'tenant.documents.commercial.columns.currentStage',
      fieldType: 'varchar',
    },
  ],
  internal: [
    {
      slug: 'active_process_name',
      labelKey: 'tenant.documents.internal.columns.activeProcessName',
      fieldType: 'varchar',
    },
    {
      slug: 'current_stage',
      labelKey: 'tenant.documents.internal.columns.currentStage',
      fieldType: 'varchar',
    },
  ],
  specjalne: [
    {
      slug: 'active_process_name',
      labelKey: 'tenant.documents.special.columns.activeProcessName',
      fieldType: 'varchar',
    },
    {
      slug: 'current_stage',
      labelKey: 'tenant.documents.special.columns.currentStage',
      fieldType: 'varchar',
    },
  ],
};

/** Pola wyliczane procesu dla danego modułu systemowego (pusta lista, gdy brak). */
export function getComputedProcessFields(moduleSlug: string | undefined): ComputedProcessField[] {
  return moduleSlug ? (COMPUTED_PROCESS_FIELDS[moduleSlug] ?? []) : [];
}

/**
 * Dokleja pola wyliczane procesu do listy pól dostępnych w pickerze „Dodaj kolumnę"
 * Kreatora Listy. Deduplikacja po `id`: jeśli katalog tenanta z jakiegoś powodu wciąż
 * zawiera dany slug (np. stary retro-seed sprzed ADR-0093), nie dublujemy wpisu —
 * pierwszeństwo ma wpis z katalogu. Doklejone pola niosą swój `fieldType` (kreator
 * listy zawęża po nim m.in. pola daty w zakładce „Kalendarz"), a pozycje katalogu
 * przechodzą bez zmian.
 */
export function mergeComputedProcessFields(
  catalog: Array<{ id: string; name: string; fieldType?: string }>,
  moduleSlug: string | undefined,
  resolveLabel: (labelKey: string) => string,
): Array<{ id: string; name: string; fieldType?: string }> {
  const present = new Set(catalog.map((f) => f.id));
  const computed = getComputedProcessFields(moduleSlug)
    .filter((f) => !present.has(f.slug))
    .map((f) => ({ id: f.slug, name: resolveLabel(f.labelKey), fieldType: f.fieldType }));
  return [...catalog, ...computed];
}

/** Pole procesu w kształcie pozycji palety Kreatora Formularza (readonly, systemowe). */
export interface ComputedProcessPaletteField {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
  isCore: boolean;
  isSystemLocked: boolean;
  isRequired: boolean;
  sortOrder: number;
}

/**
 * Buduje pozycje palety Kreatora Formularza dla pól WYLICZANYCH procesu (readonly).
 * Deduplikacja: pomija slug już obecny w katalogu (`existingSlugs`) — pierwszeństwo ma
 * katalog. `resolveLabel` = `t()`. Pusta lista dla modułów bez pól procesu.
 */
export function computedProcessPaletteFields(
  moduleSlug: string | undefined,
  existingSlugs: Set<string>,
  resolveLabel: (labelKey: string) => string,
): ComputedProcessPaletteField[] {
  return getComputedProcessFields(moduleSlug)
    .filter((cf) => !existingSlugs.has(cf.slug))
    .map((cf) => ({
      id: cf.slug,
      slug: cf.slug,
      name: resolveLabel(cf.labelKey),
      fieldType: cf.fieldType,
      isCore: true,
      isSystemLocked: true,
      isRequired: false,
      sortOrder: 9000,
    }));
}
